/**
 * Badges Card Component
 * Grid of all badges with earned/locked states
 */

import {
  Award,
  Lock,
  Scan,
  Target,
  Users,
  Flame,
  Medal,
  Gift,
  Star,
  Trophy,
  Diamond,
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { Badge } from '../types';

// Map badge icons to Lucide components
const BADGE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  scan: Scan,
  map: Target,
  'user-plus': Users,
  'person-add': Users,
  users: Users,
  people: Users,
  award: Award,
  ribbon: Award,
  calendar: Flame,
  footsteps: Medal,
  footprints: Medal,
  search: Target,
  tag: Gift,
  pricetag: Gift,
  star: Star,
  trophy: Trophy,
  diamond: Diamond,
};

interface BadgesCardProps {
  allBadges: Badge[];
  earnedCount: number;
  isBadgeEarned: (badgeId: string) => boolean;
  getBadgeEarnedDate: (badgeId: string) => string | null;
}

export function BadgesCard({
  allBadges,
  earnedCount,
  isBadgeEarned,
  getBadgeEarnedDate,
}: BadgesCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.badges.title', 'Badges')}
              </h2>
            </div>
          </div>
          <span className="text-sm text-gray-500">
            {earnedCount} / {allBadges.length} {t('profile.badges.earned', 'earned')}
          </span>
        </div>
      </div>

      {/* Badges Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {allBadges.map((badge) => {
            const earned = isBadgeEarned(badge.id);
            const earnedDate = getBadgeEarnedDate(badge.id);
            const IconComponent = BADGE_ICON_MAP[badge.icon] || Award;

            return (
              <div
                key={badge.id}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  earned
                    ? 'bg-primary-50 border-primary-200 hover:shadow-md'
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                      earned ? 'bg-primary-600 text-white shadow-lg' : 'bg-gray-300 text-gray-500'
                    }`}
                  >
                    {earned ? (
                      <IconComponent className="w-7 h-7" />
                    ) : (
                      <Lock className="w-6 h-6" />
                    )}
                  </div>
                  <div className={`font-medium ${earned ? 'text-gray-900' : 'text-gray-500'}`}>
                    {badge.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{badge.description}</div>
                  {earnedDate && (
                    <div className="text-xs text-primary-600 mt-2">
                      {t('profile.badges.earnedOn', 'Earned')} {earnedDate}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
